/**
 * Dedup store for the `Experiment Viewed` exposure event.
 *
 * Exposure fires once per user per experiment+variation: persisted in localStorage so it
 * survives reloads, and re-fires if a user is re-bucketed into a different variation. A
 * module-level in-memory map backs it up so a storage failure (Safari private mode, quota)
 * still dedupes within a single page load. All localStorage access is guarded — worst case
 * is one extra event, never a thrown error.
 */
const STORAGE_KEY = 'cltv_experiments_viewed';

const firedThisLoad = new Map<string, string>();

const readStore = (): Record<string, string> => {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        return raw ? (JSON.parse(raw) as Record<string, string>) : {};
    } catch {
        return {};
    }
};

export const hasExposureFired = (experimentName: string, variation: string): boolean => {
    if (firedThisLoad.get(experimentName) === variation) {
        return true;
    }
    return readStore()[experimentName] === variation;
};

export const markExposureFired = (experimentName: string, variation: string): void => {
    firedThisLoad.set(experimentName, variation);
    try {
        const store = readStore();
        store[experimentName] = variation;
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch {
        // in-memory guard still dedupes within this page load
    }
};
